<template>
  <div
    id="submission-toggler"
    class="submission-toggler"
  >
    <div
      class="result_toggle_item"
      :class="{'white': isTextColourLight}"
      @click.stop="toggleSubmission(inputMethods.MOUSE)"
      v-tooltip="toggleTooltip"
    >
      <div class="toggle" :class="{'switch': !slideActive.stopSubmission}">
        <div class="toggle-item">
          <aha-icon
            v-show="slideActive.stopSubmission"
            width="16"
            height="16"
            name="lockv2"
            viewBox="-4 -2 22 22"
          />
          <aha-icon
            v-show="!slideActive.stopSubmission"
            width="13"
            height="15"
            name="unlockv2"
            viewBox="0 -2 22 22"
          />
        </div>
      </div>
      <div v-if="slideActive.stopSubmission" class="closed-submission">
        <aha-icon name="warning-circle" /> <span>Submissions closed</span>
      </div>
    </div>
    <TimeSubmission
      v-if="isShowTimeSubmission"
      v-show="isPresenting"
      :isWordCloudGroupingButtonVisible="isWordCloudGroupingButtonVisible"
    />
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import Socket from '@/socket'
import Util from '@/util'
import TimeSubmission from '@/components-v2/presentation-editor/middle/TimeSubmission.vue'
import EventBus from '@/eventBus'
import SlideApi from '@/api/slide'
import { INPUT_METHODS } from '@/constant/inputMethods.constants'
import { KEYCODES } from '@/constant/keycode.constant'

const DEFAULT_TIME_CLOSE_TOOLTIP = 3000
export default {
  name: 'SubmissionToggleModal',
  components: {
    TimeSubmission,
  },
  props: {
    isTextColourLight: {
      type: Boolean,
      default: false,
    },
    isShowTimeSubmission: {
      type: Boolean,
      default: false,
    },
    isWordCloudGroupingButtonVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      isDisplayRevealTooltip: false,
      hoverTooltipWhenLock: false,
      switchToogle: false,
      isToggleEnabled: true,
      inputMethods: INPUT_METHODS,
    }
  },
  computed: {
    ...mapGetters(['isPresenting']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
    ...mapGetters('presentationHotKeyModule', ['hotKeyTrackingData']),
    hideSubmissionTooltip() {
      if (this.hoverTooltipWhenLock) {
        return this.$i18nPresenter.t('Lock submission')
      }
      return {
        show: this.isDisplayRevealTooltip,
        content: this.isDisplayRevealTooltip ? this.$i18nPresenter.t('Submission unlocked') : '',
      }
    },
    toggleTooltip() {
      let content = this.slideActive.stopSubmission
        ? this.$i18nPresenter.t('Open submission')
        : this.$i18nPresenter.t('Close submission')
      content += ' (S)'
      return content
    },
  },
  watch: {
    isDisplayRevealTooltip() {
      if (this.isDisplayRevealTooltip) {
        setTimeout(() => {
          this.isDisplayRevealTooltip = false
          setTimeout(() => {
            this.hoverTooltipWhenLock = true
          }, 300)
        }, DEFAULT_TIME_CLOSE_TOOLTIP)
      }
    },
  },
  mounted() {
    this.registerHotkeys()
  },
  destroyed() {
    this.isDisplayRevealTooltip = false
    this.unregisterHotkeys()
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),
    registerHotkeys() {
      EventBus.$on('onKeyboard', this.handleHotkey)
    },
    unregisterHotkeys() {
      EventBus.$off('onKeyboard', this.handleHotkey)
    },
    handleHotkey(e) {
      if (e.keyCode !== KEYCODES.S) return
      if (!this.isPresenting) return
      this.toggleSubmission(INPUT_METHODS.KEYBOARD)
    },
    async toggleSubmission(method) {
      if (!this.isToggleEnabled) {
        return
      }
      this.isToggleEnabled = false
      if (this.slideActive.stopSubmission) {
        this.isDisplayRevealTooltip = true
        this.hoverTooltipWhenLock = false
      }
      this.$emit('hideFullScreen')
      const params = {
        accessCode: this.presentation.uniqueAccessCode,
        socketId: Socket.getSocketId(),
      }
      params.stopSubmission = !this.slideActive.stopSubmission
      EventBus.$emit('setShowCountDown', !params.stopSubmission)
      this.updateSlideActiveAction({
        stopSubmission: params.stopSubmission,
      })
      if (!params.stopSubmission && this.slideActive.timeToAnswer) {
        const stopSubmissionTime = Util.getCurTimeStamp() +
            this.slideActive.timeToAnswer * 1000
        this.updateSlideActiveAction({
          stopSubmissionTime,
        })
        params.stopSubmissionTime = stopSubmissionTime
        params.timeToAnswer = this.slideActive.timeToAnswer
      }
      this.setSyncLoadingAction(1)
      params.presenting = this.isPresenting
      try {
        await SlideApi.updateSlideById(this.slideActive.id, params)
        setTimeout(() => {
          this.isToggleEnabled = true
        }, 200)
        const { eventCategory, presentationId, userId } = this.hotKeyTrackingData
        const eventLabel = 'Open/close submission'
        Util.mixpanelTracking(
          eventCategory,
          'Edit setting',
          eventLabel,
          !params.stopSubmission,
          {
            method,
            presentationId,
            userId,
          },
        )
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.isToggleEnabled = true
        this.setSyncLoadingAction(3)
      }
    },
  },
}
</script>

<style lang="scss" scoped>

.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
.submission-toggler {
  position: absolute;
  right: -82px;
  height: 48px;
  display: flex;
  .result_toggle_item {
    padding: 7px;
    margin-left: 0;
    cursor: pointer;
    border-radius: 50px;
    display: inline-block;
    background: rgba(255, 255, 255, 0.7);

    &.white {
      background: rgba(0, 0, 0, 0.5);
    }
    .toggle {
      width: 62px;
      height: 34px;
      position: relative;
      border-radius: 50px;
      border-radius: 50px;
      background-color: #B4BCCF;
      &.switch {
        @apply bg-primary-90;
        .toggle-item {
          left: 30px;
        }
      }
      .toggle-item {
        top: 5px;
        left: 7px;
        width: 24px;
        height: 24px;
        display: flex;
        position: absolute;
        border-radius: 100%;
        align-items: center;
        justify-content: center;
        background-color: #FFFFFF;
        transition: all 0.3s;
      }
      svg {
        color: #525F7F;
      }
    }
    .closed-submission {
      @apply bg-secondary-100;
      position: absolute;
      left: calc(100% + 10px);
      top: 0;
      display: grid;
      grid-template-columns: 20px auto;
      color: #fff;
      padding: 8px;
      border-radius: 5px;
      white-space: nowrap;
      align-items: center;
      &:after {
        content: '';
        width: 0;
        height: 0;
        left: -5px;
        position: absolute;
        top: calc(50% - 6px);
        border-top: 6px solid transparent;
        border-bottom: 6px solid transparent;
        border-right: 6px solid #282E3E;
      }
      > svg {
        @apply text-yellow-50;
      }
    }
  }
}
</style>
